import React, { useState, useCallback } from 'react';
import { Box, Button, Tooltip, CircularProgress } from '@mui/material';
import { Phone, Call, VpnKey, CallMade } from '@mui/icons-material';
import axios from 'axios';
import { global_link } from './Globallink';
import { startWhatsAppOutboundCall } from './WhatsAppWebRTCSoftphone';

/**
 * WhatsApp chat-header actions:
 *  - PSTN / softphone (existing copyPhoneNumber event)
 *  - Outbound WebRTC call (requires WhatsAppWebRTCSoftphone mounted)
 *  - Invite user to call business on WhatsApp (interactive button)
 *  - Request business-initiated call permission
 */

const api = axios.create({ baseURL: global_link });

function extractError(err, fallback) {
    const details = err?.response?.data?.details;
    if (typeof details === 'string') return details;
    if (details?.error?.message) return details.error.message;
    if (err?.response?.data?.error) return String(err.response.data.error);
    if (err?.response?.data?.hint) return String(err.response.data.hint);
    return err?.message || fallback;
}

export default function WhatsAppCallActions({
    contactNumber,
    getHeaders,
    disabled = false,
    onNotify,
    onPstnCall,
}) {
    const [loading, setLoading] = useState(null);

    const notify = useCallback((severity, message) => {
        if (typeof onNotify === 'function') onNotify({ severity, message });
    }, [onNotify]);

    const handlePstn = () => {
        if (typeof onPstnCall === 'function') {
            onPstnCall(contactNumber);
            return;
        }
        window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: contactNumber }));
    };

    const handleOutboundWebrtc = async () => {
        if (!contactNumber || loading) return;
        setLoading('outbound');
        try {
            await startWhatsAppOutboundCall(contactNumber);
        } catch (err) {
            console.error(err);
            notify('error', extractError(err, 'Failed to start WhatsApp WebRTC call'));
        } finally {
            setLoading(null);
        }
    };

    const handleInvite = async () => {
        if (!contactNumber || loading) return;
        setLoading('invite');
        try {
            await api.post(
                '/whatsapp/calling/invite',
                {
                    contact_number: contactNumber,
                    body_text: 'You can call us on WhatsApp for faster support.',
                    display_text: 'Call on WhatsApp',
                    ttl_minutes: 10080,
                },
                { headers: getHeaders() }
            );
            notify('success', 'WhatsApp call invite sent — customer can tap to call you');
        } catch (err) {
            console.error(err);
            notify('error', extractError(err, 'Failed to send WhatsApp call invite'));
        } finally {
            setLoading(null);
        }
    };

    const handlePermission = async () => {
        if (!contactNumber || loading) return;
        setLoading('permission');
        try {
            await api.post(
                '/whatsapp/calling/permission',
                {
                    contact_number: contactNumber,
                    body_text: 'Allow us to call you on WhatsApp about your request?',
                },
                { headers: getHeaders() }
            );
            notify('success', 'Call permission request sent');
        } catch (err) {
            console.error(err);
            notify('error', extractError(err, 'Failed to request call permission'));
        } finally {
            setLoading(null);
        }
    };

    if (!contactNumber) return null;

    return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Dial via softphone / PSTN">
                <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    sx={{ minWidth: 0, p: '4px 8px' }}
                    disabled={disabled}
                    onClick={handlePstn}
                >
                    <Phone sx={{ fontSize: '1rem' }} />
                </Button>
            </Tooltip>

            <Tooltip title="Call customer on WhatsApp (WebRTC — needs call permission)">
                <span>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{ minWidth: 0, p: '4px 8px' }}
                        disabled={disabled || !!loading}
                        onClick={handleOutboundWebrtc}
                    >
                        {loading === 'outbound'
                            ? <CircularProgress size={14} color="inherit" />
                            : <CallMade sx={{ fontSize: '1rem' }} />}
                    </Button>
                </span>
            </Tooltip>

            <Tooltip title="Send WhatsApp call button (customer calls you)">
                <span>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ minWidth: 0, p: '4px 8px' }}
                        disabled={disabled || !!loading}
                        onClick={handleInvite}
                    >
                        {loading === 'invite'
                            ? <CircularProgress size={14} color="inherit" />
                            : <Call sx={{ fontSize: '1rem' }} />}
                    </Button>
                </span>
            </Tooltip>

            <Tooltip title="Request permission for business-initiated WhatsApp calls">
                <span>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        sx={{ minWidth: 0, p: '4px 8px' }}
                        disabled={disabled || !!loading}
                        onClick={handlePermission}
                    >
                        {loading === 'permission'
                            ? <CircularProgress size={14} color="inherit" />
                            : <VpnKey sx={{ fontSize: '1rem' }} />}
                    </Button>
                </span>
            </Tooltip>
        </Box>
    );
}